import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, GiftIcon, Loader2Icon, PhoneIcon } from 'lucide-react';
import { services } from '../data/services';
import { site } from '../data/site';
type EstimateBuilderProps = {
  selected: string[];
  onToggle: (id: string) => void;
};
const timelines = ['As soon as possible', 'Within 2 weeks', 'Within a month', 'Just planning ahead'];
const properties = ['Single-family home', 'Condo / townhome', 'Rental property', 'Business'];
const stepLabels = ['What needs fixing', 'Timing & property', 'Where to send it'];
export function EstimateBuilder({
  selected,
  onToggle
}: EstimateBuilderProps) {
  const [step, setStep] = useState(0);
  const [timeline, setTimeline] = useState('');
  const [property, setProperty] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    zip: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState('');
  const canNext = step === 0 ? selected.length > 0 : step === 1 ? Boolean(timeline && property) : true;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, '').length < 10 || form.zip.length < 5) {
      setError('Add your name, phone number and 5-digit ZIP so we can send your estimate.');
      return;
    }
    setError('');
    setStatus('submitting');
    window.setTimeout(() => setStatus('done'), 1000);
  };
  const chip = (active: boolean) => `flex items-center gap-2 rounded-lg border px-4 py-3 text-left text-[15px] font-semibold transition-colors duration-150 ${active ? 'border-brand bg-brand text-white' : 'border-white/15 bg-white/5 text-white hover:border-white/40'}`;
  return <section id="estimate" className="bg-ink py-20 lg:py-28" data-id="element-64" data-mp="157zwxtpgq12z">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16 lg:px-8" data-id="element-65" data-mp="10ghxxb702akn">
        <div data-id="element-66" data-mp="2uobwu0kwvecz">
          <h2 className="font-display text-5xl font-extrabold uppercase leading-[0.95] text-white lg:text-6xl" data-id="element-67" data-mp="2untwarr7udz9">
            Build your repair list.
            <br data-id="element-68" data-mp="2zp9f0d5x88eh" />
            <span className="text-brand" data-id="element-69" data-mp="2kyac9irroe2t">Get one price.</span>
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-white/75" data-id="element-70" data-mp="2kw69j45ltl44">
            Bundle every fix around the house into a single visit. Answer three quick questions and we’ll send a
            written estimate within 24 hours.
          </p>
          <div className="mt-8 flex items-start gap-4 rounded-xl border border-brand/40 bg-brand/10 p-5" data-id="element-71" data-mp="2fzftuhxporj8">
            <GiftIcon className="mt-0.5 h-6 w-6 shrink-0 text-brand" aria-hidden="true" data-id="element-72" data-mp="220g5zci0wgo4" />
            <p className="text-white" data-id="element-73" data-mp="26xprwi67ea77">
              <strong className="font-display text-xl font-bold" data-id="element-74" data-mp="26yrglpg56v7e">$50 off your first repair</strong>
              <span className="block text-sm text-white/70" data-id="element-75" data-mp="8uo6vjxn3gv">
                Automatically applied when you request your estimate online.
              </span>
            </p>
          </div>
        </div>

        <div className="rounded-2xl bg-ink-soft p-6 ring-1 ring-white/10 sm:p-8" data-id="element-76" data-mp="1ppggewnwezzq">
          {status === 'done' ? <div className="flex flex-col items-center py-12 text-center" role="status" data-id="element-77" data-mp="1ko9a721em8v6">
              <span className="grid h-16 w-16 place-items-center rounded-full bg-brand text-white" data-id="element-78" data-mp="1zh31rltyafg7">
                <CheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-79" data-mp="1um6z7rr389ou" />
              </span>
              <h3 className="mt-5 font-display text-4xl font-bold text-white" data-id="element-80" data-mp="28dtvvsoxzk85">Estimate request received</h3>
              <p className="mt-2 max-w-sm text-white/75" data-id="element-81" data-mp="23io8epfw5xhy">
                Thanks {form.name.split(' ')[0]} — we’ll text {form.phone} within 24 hours. Your $50 credit is
                locked in.
              </p>
              <a href={site.phoneHref} className="mt-6 flex items-center gap-2 font-display text-2xl font-bold text-brand" data-id="element-82" data-mp="nztiazatnnew">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-83" data-mp="j2kx0pudn39f" /> Can’t wait? {site.phone}
              </a>
            </div> : <>
              <ol className="grid grid-cols-3 gap-2" aria-label="Estimate progress" data-id="element-84" data-mp="3b7hgi2sgyn5n">
                {stepLabels.map((label, i) => <li key={label} data-id="element-85" data-mp="3g855yk11uvxr" data-mpi={i}>
                    <div className={`h-1.5 rounded-full ${i <= step ? 'bg-brand' : 'bg-white/15'}`} data-id="element-86" data-mp="3k8yxdndpasve" />
                    <span className={`mt-2 hidden text-xs font-semibold sm:block ${i === step ? 'text-white' : 'text-white/50'}`} aria-current={i === step ? 'step' : undefined} data-id="element-87" data-mp="2niy8lodc0ce3">
                      {label}
                    </span>
                  </li>)}
              </ol>

              <div className="mt-6 min-h-[340px]" data-id="element-88" data-mp="24sqaxlseie5a">
                <AnimatePresence mode="wait" initial={false} data-id="element-89" data-mp="24ryek8qzccdy">
                  <motion.div key={step} initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-90" data-mp="28t0fcu0jowi1">
                    {step === 0 && <fieldset data-id="element-91" data-mp="1sbfiv8qdsi5a">
                        <legend className="font-display text-2xl font-bold text-white" data-id="element-92" data-mp="1nb1atqos5879">
                          What needs fixing? <span className="text-white/50" data-id="element-93" data-mp="1ibolmzafrp1i">Pick all that apply.</span>
                        </legend>
                        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-94" data-mp="1djwv2ora5jbd">
                          {services.map(s => {
                      const active = selected.includes(s.id);
                      return <button key={s.id} type="button" aria-pressed={active} onClick={() => onToggle(s.id)} className={chip(active)} data-id="element-95" data-mp="14hxdnvccsd7w">
                                <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${active ? 'border-white bg-white text-brand' : 'border-white/40'}`} data-id="element-96" data-mp="px1kf7osyi8z">
                                  {active && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-97" data-mp="13prb3pfa0bcf" />}
                                </span>
                                <span className="truncate" data-id="element-98" data-mp="18n16dlpcjh86">{s.name}</span>
                              </button>;
                    })}
                        </div>
                      </fieldset>}

                    {step === 1 && <div className="space-y-7" data-id="element-99" data-mp="1mm0yj3c2ie93">
                        <fieldset data-id="element-100" data-mp="1re0tehda6gyt">
                          <legend className="font-display text-2xl font-bold text-white" data-id="element-101" data-mp="1rf2dfbc9ydso">When do you need it done?</legend>
                          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-102" data-mp="3c2uzck54itxc">
                            {timelines.map(t => <button key={t} type="button" aria-pressed={timeline === t} onClick={() => setTimeline(t)} className={chip(timeline === t)} data-id="element-103" data-mp="3luiwn1od356q">
                                {t}
                              </button>)}
                          </div>
                        </fieldset>
                        <fieldset data-id="element-104" data-mp="2y4ew536gzljy">
                          <legend className="font-display text-2xl font-bold text-white" data-id="element-105" data-mp="2y3d9s2k07cmx">What type of property?</legend>
                          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-106" data-mp="hidq1c64x1e3">
                            {properties.map(p => <button key={p} type="button" aria-pressed={property === p} onClick={() => setProperty(p)} className={chip(property === p)} data-id="element-107" data-mp="26xoh7l81i49j">
                                {p}
                              </button>)}
                          </div>
                        </fieldset>
                      </div>}

                    {step === 2 && <form id="estimate-form" onSubmit={submit} noValidate data-id="element-108" data-mp="2zlkash2bbg23">
                        <h3 className="font-display text-2xl font-bold text-white" data-id="element-109" data-mp="34l6nmpe8auad">Where should we send your estimate?</h3>
                        <p className="mt-1 text-sm text-white/60" data-id="element-110" data-mp="158m0vykiylfa">
                          {selected.length} service{selected.length > 1 ? 's' : ''} · {timeline} · {property}
                        </p>
                        <div className="mt-5 grid gap-3" data-id="element-111" data-mp="3nhp015ynp7j6">
                          {([{
                      key: 'name',
                      label: 'Full name',
                      type: 'text',
                      auto: 'name'
                    }, {
                      key: 'phone',
                      label: 'Mobile number',
                      type: 'tel',
                      auto: 'tel'
                    }, {
                      key: 'zip',
                      label: 'ZIP code',
                      type: 'text',
                      auto: 'postal-code'
                    }] as const).map(f => <label key={f.key} className="block" data-id="element-112" data-mp="68sb4x8je9ec">
                              <span className="mb-1.5 block text-sm font-semibold text-white/80" data-id="element-113" data-mp="67h6ywb6izmp">{f.label}</span>
                              <input type={f.type} autoComplete={f.auto} inputMode={f.key === 'zip' ? 'numeric' : undefined} maxLength={f.key === 'zip' ? 5 : undefined} value={form[f.key]} onChange={e => setForm(prev => ({
                        ...prev,
                        [f.key]: e.target.value
                      }))} className="w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-white outline-none transition-colors duration-150 placeholder:text-white/40 focus:border-brand focus:ring-2 focus:ring-brand/30" data-id="element-114" data-mp="3sgieb0lrp2ke" />
                            </label>)}
                        </div>
                        {error && <p className="mt-3 text-sm font-medium text-red-400" role="alert" data-id="element-115" data-mp="3fevhf3q33lft">
                            {error}
                          </p>}
                      </form>}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-6 flex items-center justify-between gap-4 border-t border-white/10 pt-6" data-id="element-116" data-mp="200r17qq5oht5">
                {step > 0 ? <button type="button" onClick={() => setStep(s => s - 1)} className="flex items-center gap-1.5 font-semibold text-white/70 transition-colors duration-150 hover:text-white" data-id="element-117" data-mp="agzji99nfhe1">
                    <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-118" data-mp="pvdhz1yb6vc" /> Back
                  </button> : <span className="text-sm text-white/50" data-id="element-119" data-mp="3nvw5g9nvh13l">
                    {selected.length === 0 ? 'Select at least one service' : `${selected.length} selected`}
                  </span>}

                {step < 2 ? <button type="button" disabled={!canNext} onClick={() => setStep(s => s + 1)} className="flex items-center gap-2 rounded-lg bg-brand px-6 py-3 font-display text-xl font-bold text-white transition-colors duration-150 hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/40" data-id="element-120" data-mp="3e02kc1w37za7">
                    Continue <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="1apz8kfzfqq1b" />
                  </button> : <button type="submit" form="estimate-form" disabled={status === 'submitting'} className="flex items-center gap-2 rounded-lg bg-brand px-6 py-3 font-display text-xl font-bold text-white transition-colors duration-150 hover:bg-brand-dark disabled:opacity-80" data-id="element-122" data-mp="15ptekt5p0rg2">
                    {status === 'submitting' ? <>
                        <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-123" data-mp="1on4b45zxx6rt" /> Sending…
                      </> : 'Send My Free Estimate'}
                  </button>}
              </div>
            </>}
        </div>
      </div>
    </section>;
}